import React, { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Tooltip,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { Close as CloseIcon, RemoveCircleOutline as WriteOffIcon } from '@mui/icons-material';
import { ActionButton } from '../../common/Table';
import { PhysicalCountService } from '../../../api/physicalCountCompat';
import type {
  PhysicalCountSession,
  PhysicalCountDetailItem,
} from '../../../types/physicalCount.types';
import { INVENTORY_PERMISSIONS } from '../../../constants/permissions.constants';
import { usePermission } from '../../../hooks/usePermission';
import WriteOffConfirmationModal from '../../modals/confirmation/WriteOffConfirmationModal';
import CommonPagination from '../../common/Pagination';
import { usePhysicalCountMissingItems } from '../physicalCount/usePhysicalCountMissingItems';
import { extractErrorMessage } from '../../../utils/errorHelpers';
import { formatDateTime } from '../../../utils/formatters/date';
import type { WriteOffMissingResultItemDto } from '../../../api/models/WriteOffMissingResultItemDto';

const formatDisplayDateTime = (dateStr?: string) => {
  if (dateStr == null || dateStr === '') return '-';
  return formatDateTime(dateStr) || '-';
};

export interface PhysicalCountWriteOffMissingModalProps {
  open: boolean;
  onClose: () => void;
  session: PhysicalCountSession | null;
  /** Refrescar listado de sesiones en la página */
  onSuccess?: () => void;
  /** Tras cerrar el modal (p. ej. confirmación masiva), notificar en la página */
  onNotify?: (message: string, severity: 'success' | 'error' | 'warning' | 'info') => void;
}

const PhysicalCountWriteOffMissingModal: React.FC<PhysicalCountWriteOffMissingModalProps> = ({
  open,
  onClose,
  session,
  onSuccess,
  onNotify,
}) => {
  const theme = useTheme();
  const primaryCtaButtonSx = {
    textTransform: 'none' as const,
    fontWeight: 600,
    fontSize: '0.8125rem',
    color: 'common.white',
    background: theme.gradients.primaryCta,
    boxShadow: '0 2px 8px rgba(6, 182, 212, 0.35)',
    '&:hover': {
      background: theme.gradients.primaryCtaHover,
      boxShadow: '0 4px 12px rgba(6, 182, 212, 0.45)',
    },
    '&:disabled': {
      opacity: 0.55,
      color: 'common.white',
    },
  };
  const canWriteOffMissing = usePermission(INVENTORY_PERMISSIONS.PHYSICAL_COUNT.WRITE_OFF_MISSING);
  const {
    summary,
    summaryError,
    missingItems,
    itemsError,
    currentPage,
    itemsPerPage,
    totalItems,
    initialLoading,
    itemsLoading,
    refreshAfterWriteOff,
    handlePageChange,
    handleItemsPerPageChange,
  } = usePhysicalCountMissingItems({ open, session });

  const [bulkWriteOffLoading, setBulkWriteOffLoading] = useState(false);
  const [singleConfirm, setSingleConfirm] = useState<PhysicalCountDetailItem | null>(null);
  const [singleConfirmLoading, setSingleConfirmLoading] = useState(false);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [writeOffFeedback, setWriteOffFeedback] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setWriteOffFeedback(null);
      setSingleConfirm(null);
      setBulkConfirmOpen(false);
    }
  }, [open]);

  const tableContainerSx = {
    borderRadius: 1,
    bgcolor: 'background.paper',
    borderColor: 'divider',
    backgroundImage: 'none',
    boxShadow: 'none',
    '& .MuiTableHead-root .MuiTableCell-root': {
      bgcolor: (t: typeof theme) =>
        t.palette.mode === 'dark' ? alpha(t.palette.common.white, 0.06) : t.palette.grey[100],
      color: 'text.secondary',
      fontWeight: 600,
      fontSize: '0.8125rem',
      borderBottomColor: 'divider',
    },
    '& .MuiTableBody-root .MuiTableCell-root': {
      color: 'text.primary',
      borderBottomColor: 'divider',
      fontSize: '0.875rem',
    },
    '& .MuiTableRow-root:last-of-type .MuiTableCell-root': {
      borderBottom: 'none',
    },
  };

  const handleConfirmSingleWriteOff = useCallback(
    async (comment?: string) => {
      const sid = session?.id;
      const did = singleConfirm?.id;
      if (sid == null || did == null) return;
      setSingleConfirmLoading(true);
      setWriteOffFeedback(null);
      try {
        const res = await PhysicalCountService.sessionsWriteOffMissing(sid, {
          discrepancyId: did,
          ...(comment ? { comment } : {}),
        });
        setSingleConfirm(null);
        onSuccess?.();
        const r = res.results?.[0];
        const newSummary = await refreshAfterWriteOff(sid, currentPage);
        if (r?.status === 'skipped') {
          setWriteOffFeedback(r.message ?? 'Esta línea ya estaba confirmada.');
        } else if ((newSummary?.pendingMissingCount ?? 0) === 0) {
          onNotify?.(
            'Faltante confirmado. No quedan faltantes pendientes en esta sesión.',
            'success',
          );
          onClose();
        } else {
          setWriteOffFeedback('Faltante confirmado con éxito.');
        }
      } catch (e: unknown) {
        setWriteOffFeedback(extractErrorMessage(e, 'No se pudo confirmar el faltante.'));
        setSingleConfirm(null);
      } finally {
        setSingleConfirmLoading(false);
      }
    },
    [
      session?.id,
      singleConfirm?.id,
      onSuccess,
      onNotify,
      onClose,
      refreshAfterWriteOff,
      currentPage,
    ],
  );

  const handleConfirmBulkWriteOff = useCallback(
    async (comment?: string) => {
      const sid = session?.id;
      if (sid == null) return;
      setBulkWriteOffLoading(true);
      setWriteOffFeedback(null);
      try {
        const res = await PhysicalCountService.sessionsWriteOffMissing(sid, {
          allMissing: true,
          ...(comment ? { comment } : {}),
        });
        const results = res.results ?? [];
        const ok = results.filter((x: WriteOffMissingResultItemDto) => x.status === 'ok').length;
        const skipped = results.filter(
          (x: WriteOffMissingResultItemDto) => x.status === 'skipped',
        ).length;
        const errors = results.filter((x: WriteOffMissingResultItemDto) => x.status === 'error');
        const parts = [
          `Procesadas: ${ok} faltantes confirmados.`,
          skipped > 0 ? `${skipped} omitidas (ya registradas).` : null,
          errors.length > 0
            ? `Errores (${errors.length}): ${errors
                .slice(0, 5)
                .map((e: WriteOffMissingResultItemDto) => `#${e.discrepancyId} ${e.message ?? ''}`)
                .join('; ')}${errors.length > 5 ? '…' : ''}`
            : null,
        ].filter(Boolean);
        const message = parts.join(' ');
        setBulkConfirmOpen(false);
        onNotify?.(message, errors.length > 0 ? 'warning' : 'success');
        onSuccess?.();
        onClose();
      } catch (e: unknown) {
        const msg = extractErrorMessage(e, 'No se pudo completar la operación.');
        setWriteOffFeedback(msg);
        onNotify?.(msg, 'error');
      } finally {
        setBulkWriteOffLoading(false);
      }
    },
    [session?.id, onNotify, onSuccess, onClose],
  );

  if (session == null) return null;

  const displaySummary = summary;
  const pendingMissingCount = displaySummary?.pendingMissingCount ?? 0;
  const missingCount = displaySummary?.missingCount ?? session.missingCount ?? 0;

  const showActions =
    (displaySummary?.status ?? session.status) === 'finalized' &&
    canWriteOffMissing &&
    missingCount > 0;

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            overflow: 'hidden',
            bgcolor: 'background.paper',
            backgroundImage: 'none',
            border: '1px solid',
            borderColor: 'divider',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            background: theme.gradients.primaryCta,
            color: 'common.white',
            fontWeight: 600,
          }}
        >
          <span>Confirmar faltantes: {session.code}</span>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{ color: 'common.white' }}
            aria-label="Cerrar"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0, bgcolor: 'background.default' }}>
          {initialLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
                {summaryError != null && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    {summaryError}
                  </Alert>
                )}
                <Typography variant="body2" color="text.secondary">
                  Sucursal:{' '}
                  <Typography
                    component="span"
                    variant="body2"
                    fontWeight={600}
                    color="text.primary"
                  >
                    {displaySummary?.locationName ?? session.locationName ?? '-'}
                  </Typography>
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Fecha sesión:{' '}
                  {formatDisplayDateTime(displaySummary?.sessionDate ?? session.sessionDate)}
                </Typography>
                {!canWriteOffMissing && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    No tiene permiso para confirmar faltantes. Solo puede consultar la lista.
                  </Alert>
                )}
                {(displaySummary?.status ?? session.status) !== 'finalized' && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    Solo las sesiones finalizadas permiten confirmar faltantes en inventario.
                  </Alert>
                )}
              </Box>

              <Box sx={{ p: 3 }}>
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    mb: 1,
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={600} sx={{ color: 'error.main' }}>
                    Faltantes ({missingCount})
                    {pendingMissingCount > 0 && pendingMissingCount < missingCount
                      ? ` · ${pendingMissingCount} pendientes`
                      : null}
                  </Typography>
                  {showActions && pendingMissingCount > 0 && (
                    <Button
                      variant="contained"
                      size="small"
                      disableElevation
                      disabled={bulkWriteOffLoading || singleConfirmLoading}
                      onClick={() => setBulkConfirmOpen(true)}
                      sx={primaryCtaButtonSx}
                    >
                      Confirmar todos los faltantes ({pendingMissingCount})
                    </Button>
                  )}
                </Box>
                {writeOffFeedback != null && (
                  <Alert severity="info" sx={{ mb: 1.5 }} onClose={() => setWriteOffFeedback(null)}>
                    {writeOffFeedback}
                  </Alert>
                )}
                {itemsError != null && (
                  <Alert severity="error" sx={{ mb: 1.5 }}>
                    {itemsError}
                  </Alert>
                )}
                {missingCount === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No hay piezas faltantes en esta sesión.
                  </Typography>
                ) : itemsLoading && missingItems.length === 0 ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={28} />
                  </Box>
                ) : (
                  <>
                    <TableContainer component={Paper} variant="outlined" sx={tableContainerSx}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Código único</TableCell>
                            <TableCell>Código</TableCell>
                            <TableCell>Producto</TableCell>
                            <TableCell align="right">Esperado</TableCell>
                            <TableCell align="right">Escaneado</TableCell>
                            <TableCell>Baja en inventario</TableCell>
                            {showActions && (
                              <TableCell align="right" sx={{ width: 48, px: 1 }}>
                                Acción
                              </TableCell>
                            )}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {missingItems.map((row) => (
                            <TableRow key={row.id ?? row.productUniqueCode ?? row.productCode}>
                              <TableCell>{row.productUniqueCode ?? '-'}</TableCell>
                              <TableCell>{row.productCode}</TableCell>
                              <TableCell>{row.productName ?? '-'}</TableCell>
                              <TableCell align="right">{row.expectedQty ?? '-'}</TableCell>
                              <TableCell align="right">{row.actualQty ?? '-'}</TableCell>
                              <TableCell>
                                {row.writtenOffAt ? (
                                  <Typography variant="caption" color="text.secondary">
                                    {formatDisplayDateTime(row.writtenOffAt)}
                                    {row.writtenOffBy != null && row.writtenOffBy !== ''
                                      ? ` · ${row.writtenOffBy}`
                                      : ''}
                                  </Typography>
                                ) : (
                                  <Typography variant="caption" color="text.secondary">
                                    Pendiente
                                  </Typography>
                                )}
                              </TableCell>
                              {showActions && (
                                <TableCell align="right" sx={{ width: 48, px: 1 }}>
                                  {!row.writtenOffAt && row.id != null ? (
                                    <Tooltip title="Confirmar faltante" arrow>
                                      <span>
                                        <ActionButton
                                          size="small"
                                          colorVariant="error"
                                          disabled={bulkWriteOffLoading || singleConfirmLoading}
                                          onClick={() => setSingleConfirm(row)}
                                          aria-label={`Confirmar faltante: ${row.productCode ?? row.productUniqueCode ?? row.id}`}
                                        >
                                          <WriteOffIcon fontSize="small" />
                                        </ActionButton>
                                      </span>
                                    </Tooltip>
                                  ) : null}
                                </TableCell>
                              )}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    {totalItems > 0 && (
                      <Box sx={{ mt: 1.5 }}>
                        <CommonPagination
                          currentPage={currentPage}
                          itemsPerPage={itemsPerPage}
                          totalItems={totalItems}
                          onPageChange={handlePageChange}
                          onItemsPerPageChange={handleItemsPerPageChange}
                          itemLabelPlural="faltantes"
                        />
                      </Box>
                    )}
                  </>
                )}
              </Box>
            </>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            p: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            backgroundImage: 'none',
          }}
        >
          <Button
            onClick={onClose}
            variant="outlined"
            sx={(t) => ({
              textTransform: 'none',
              borderColor: 'divider',
              color: 'text.secondary',
              backgroundColor: t.palette.background.paper,
              '&:hover': {
                backgroundColor: t.palette.action.hover,
                color: 'text.primary',
                borderColor: 'divider',
              },
            })}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <WriteOffConfirmationModal
        open={singleConfirm != null}
        onClose={() => {
          if (!singleConfirmLoading) setSingleConfirm(null);
        }}
        onConfirm={handleConfirmSingleWriteOff}
        itemName={singleConfirm?.productName ?? singleConfirm?.productCode}
        itemCode={singleConfirm?.productCode}
        itemUniqueCode={singleConfirm?.productUniqueCode}
        isLoading={singleConfirmLoading}
      />

      <WriteOffConfirmationModal
        open={bulkConfirmOpen}
        onClose={() => {
          if (!bulkWriteOffLoading) setBulkConfirmOpen(false);
        }}
        onConfirm={handleConfirmBulkWriteOff}
        pendingCount={pendingMissingCount}
        isLoading={bulkWriteOffLoading}
      />
    </>
  );
};

export default PhysicalCountWriteOffMissingModal;
